import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const LOGO_WHITE_URL = "https://customer-assets.emergentagent.com/job_mayker-victoria/artifacts/cfvu1wmg_Mayker_Logo_Standard_Colour_Rev.png";

const navLinks = [
  { name: "Life Insurance", path: "/life-insurance" },
  { name: "Group Benefits", path: "/group-benefits" },
  { name: "Group Benefits Review", path: "/group-benefits-review" },
  { name: "Financial Planning", path: "/financial-planning" },
  { name: "TFSA & Investments", path: "/tfsa-investments" },
  { name: "About", path: "/about" },
  { name: "Blog", path: "/blog" },
];

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <nav 
        data-testid="navigation"
        className={`fixed top-0 left-0 right-0 z-50 nav-blur transition-all duration-300 ${isScrolled ? "shadow-lg" : ""}`}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-18">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img 
                src={LOGO_WHITE_URL} 
                alt="Mayker Financial" 
                className="h-9 lg:h-10"
                data-testid="nav-logo"
              />
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-[13px] font-medium transition-colors ${
                    location.pathname === link.path 
                      ? "text-[#C9A84C]" 
                      : "text-white/70 hover:text-white"
                  }`}
                  data-testid={`nav-link-${link.path.replace('/', '')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Right side - Phone & CTA */}
            <div className="flex items-center gap-3 lg:gap-4">
              <a 
                href="tel:7788361962" 
                className="hidden md:flex items-center gap-1.5 text-xs text-white/50 hover:text-[#C9A84C] transition-colors"
                data-testid="nav-phone"
              >
                <Phone className="w-3.5 h-3.5" />
                778-836-1962
              </a>
              <Link 
                to="/contact"
                className="hidden sm:block bg-[#C9A84C] text-[#0C1B33] text-xs lg:text-[13px] font-medium tracking-wide px-4 lg:px-5 py-2 lg:py-2.5 rounded hover:bg-[#E8CC80] transition-colors"
                data-testid="nav-cta"
              >
                Get a Free Quote →
              </Link>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-white"
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Drawer */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" data-testid="mobile-menu">
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="absolute top-16 left-0 right-0 mobile-menu-overlay border-t border-white/10">
            <div className="p-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-3 px-4 rounded text-base font-medium ${
                    location.pathname === link.path 
                      ? "text-[#C9A84C] bg-white/5" 
                      : "text-white/80 hover:bg-white/5"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-white/10 space-y-3">
                <a 
                  href="tel:7788361962"
                  className="flex items-center gap-2 py-2 px-4 text-white/70"
                >
                  <Phone className="w-4 h-4" />
                  778-836-1962
                </a>
                <Link 
                  to="/contact"
                  className="block text-center bg-[#C9A84C] text-[#0C1B33] font-medium py-3 px-4 rounded hover:bg-[#E8CC80]"
                >
                  Get a Free Quote →
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
